import { useCallback, useEffect, useState } from 'react'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import {
  IconAdjustmentsHorizontal,
  IconSortAscendingLetters,
  IconSortDescendingLetters,
} from '@tabler/icons-react'
import { WhatsappStatus } from '@/interfaces/whatsapp'
import { listWhatsappConnectionQuery } from '@/queries/whatsapp.query'
import { toast } from 'sonner'
import { isSocket } from '@/utils/is-socket'
import { SocketEvent } from '@/constants/socket-events'
import { WhatsAppEvent } from '@/constants/whatsapp-events'
import { useSocket } from '@/hooks/use-socket'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Main } from '@/components/layout/main'
import { ConnectionCard } from './components/connection-card'
import { ConnectionsDialogs } from './components/connections-dialogs'
import { TasksPrimaryButtons } from './components/connections-primary-buttons'
import { EmptyState } from './components/empty-state'
import {
  selectCurrent,
  selectSetOpen,
  selectSetQrCode,
  useConnectionStore,
} from './store'

const appText = new Map<string, string>([
  ['all', 'Todas'],
  ['connected', 'Conectadas'],
  ['notConnected', 'Desconectadas'],
])

type StatusEvent = {
  status: string
  whatsappId: string
}

export default function Connections() {
  const [sort, setSort] = useState('ascending')
  const [appType, setAppType] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const setQrCode = useConnectionStore(selectSetQrCode)
  const setOpen = useConnectionStore(selectSetOpen)
  const current = useConnectionStore(selectCurrent)
  const { socket } = useSocket()
  const { data } = useSuspenseQuery(listWhatsappConnectionQuery)
  const queryClient = useQueryClient()
  const handleQrCode = useCallback((qrCode: { base64Qr: string }) => {
    setQrCode(qrCode.base64Qr)
  }, [])

  const handleStatus = useCallback(
    ({ status, whatsappId }: StatusEvent) => {
      if (whatsappId === current?.id) {
        if (status === 'open') {
          toast.info('QR Code lido')
          toast.success('Conectado ao whatsapp!')
          setOpen(null)
          setQrCode(null)
          socket?.emit(SocketEvent.LEAVE_QR_CODE, { whatsappId: current?.id })
          setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: listWhatsappConnectionQuery.queryKey,
            })
          }, 500)
        }
      }
    },
    [current, setOpen, setQrCode, socket, queryClient]
  )

  const filteredConnections = data
    .sort((a, b) =>
      sort === 'ascending'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    )
    .filter((app) =>
      appType === 'connected'
        ? app.status === WhatsappStatus.CONNECTED
        : appType === 'notConnected'
          ? app.status === WhatsappStatus.DISCONNECTED
          : true
    )
    .filter((app) => app.name.toLowerCase().includes(searchTerm.toLowerCase()))

  useEffect(() => {
    if (isSocket(socket)) {
      socket.on(WhatsAppEvent.QRCODE_UPDATED, handleQrCode)
      socket.on(WhatsAppEvent.CONNECTION_UPDATE, handleStatus)
    }

    return () => {
      if (isSocket(socket)) {
        socket.off(WhatsAppEvent.QRCODE_UPDATED, handleQrCode)
        socket.off(WhatsAppEvent.CONNECTION_UPDATE, handleStatus)
      }
    }
  }, [socket, handleQrCode, handleStatus])

  return (
    <>
      <Main>
        <div className='mb-2 flex flex-wrap items-center justify-between gap-x-4 space-y-2'>
          <div>
            <h2 className='text-2xl font-bold tracking-tight'>Conexões</h2>
            <p className='text-muted-foreground'>
              Aqui você gerencia as conexões com whatsapp.
            </p>
          </div>
          <TasksPrimaryButtons />
        </div>
        <div className='my-4 flex items-end justify-between sm:my-0 sm:items-center'>
          <div className='flex flex-col gap-4 sm:my-4 sm:flex-row'>
            <Input
              placeholder='Filtrar conexões...'
              className='h-9 w-40 lg:w-[250px]'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Select value={appType} onValueChange={setAppType}>
              <SelectTrigger className='w-[10rem]'>
                <SelectValue>{appText.get(appType)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>Todas</SelectItem>
                <SelectItem value='connected'>Conectadas</SelectItem>
                <SelectItem value='notConnected'>Desconectadas</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className='w-16'>
              <SelectValue>
                <IconAdjustmentsHorizontal size={18} />
              </SelectValue>
            </SelectTrigger>
            <SelectContent align='end'>
              <SelectItem value='ascending'>
                <div className='flex items-center gap-4'>
                  <IconSortAscendingLetters size={16} />
                  <span>Ascending</span>
                </div>
              </SelectItem>
              <SelectItem value='descending'>
                <div className='flex items-center gap-4'>
                  <IconSortDescendingLetters size={16} />
                  <span>Descending</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Separator className='shadow' />
        {data.length === 0 ? (
          <div className='pt-4'>
            <EmptyState />
          </div>
        ) : filteredConnections.length === 0 ? (
          <div className='mt-4 rounded-lg border bg-gray-50 py-8 text-center dark:bg-gray-900'>
            <h3 className='mb-1 text-lg font-medium'>
              Nenhum resultado encontrado
            </h3>
            <p className='text-sm text-gray-500'>
              Tente ajustar os filtros ou termos de busca
            </p>
          </div>
        ) : (
          <ul className='faded-bottom no-scrollbar grid gap-4 overflow-auto pb-16 pt-4 md:grid-cols-2 lg:grid-cols-3'>
            {filteredConnections.map((whatsapp) => (
              <ConnectionCard key={whatsapp.id} whatsapp={whatsapp} />
            ))}
          </ul>
        )}
      </Main>

      <ConnectionsDialogs />
    </>
  )
}
